import { useMemo, useReducer, useState } from 'react';
import {
  useAtrTrail,
  useAtrTrailEnabled,
  useBreakPrice,
  useBreakPriceEnabled,
  useCandleMagnitude,
  useCandleMagnitudeEnabled,
  useCandleTrail,
  useCandleTrailEnabled,
  useExitTactics,
  useGrabCents,
  useGrabCentsDetails,
  useGrabCentsEnabled,
  useGrabCentsMode,
  useGreenToRedCandles,
  useGreenToRedCandlesEnabled,
  useGreenToRedPositions,
  useGreenToRedPositionsEnabled,
  useLossLimit,
  useLossLimitEnabled,
  useMfeRetracement,
  useMfeRetracementEnabled,
  usePositionBreakPrice,
  usePositionBreakPriceEnabled,
  usePositionPriceTrail,
  usePositionPriceTrailEnabled,
  usePreserveProfit,
  usePreserveProfitEnabled,
  useRatchetBracket,
  useRatchetBracketEnabled,
  useUnderlyingPriceExit,
  useUnderlyingPriceExitEnabled,
  useUnderlyingPriceTrail,
  useUnderlyingPriceTrailEnabled,
} from '../../../forms/tradeplans/tradingPlanFormComponents/hooks/tactics/ExitTacticHooks';
import {
  useCandleBreak,
  useLimitOrder,
  useMarketOrderEnabled,
} from '../../../forms/tradeplans/tradingPlanFormComponents/hooks/tactics/EntryTacticHooks';
import { useBreak } from '../../../forms/tradeplans/tradingPlanFormComponents/hooks/setup/SetupHooks';
import useTopReducer from '../../../forms/tradeplans/tradingPlanFormComponents/hooks/topReducer';
import {
  useBuyOrSellOptions,
  useExpirationDates,
  useHugCurrentPrice,
  useHugCurrentPriceEnabled,
  useITM,
  useNotExpiringToday,
  useOptionManagement,
  useOptionPriceRange,
  useOptionPriceRangeEnabled,
  useOTM,
  useTightSpreadPct,
  useTightSpreadCentsEnabled,
  useViewOptionManagementEnabled,
  useTightSpreadPctEnabled,
  useTightSpreadCents,
  useOtm1Enabled,
  useOtm1,
  useItm1Enabled,
  useItm1,
  useQtyRangeEnabled,
  useDollarRangeEnabled,
  useQtyRange,
  useDollarRange,
} from '../../../forms/tradeplans/tradingPlanFormComponents/hooks/optionmanagement/OptionManagmentHooks';
import { useRestartSettings } from '../../../forms/tradeplans/tradingPlanFormComponents/hooks/restartsettings/RestartSettingsHooks';

// NOTE why doesn't frontRun or evaluateQuotes appear in here???  Is that why I can't get the value from UserPrefs??
const useUserPreferences = () => {
  //
  const [userAlerts, setUserAlerts] = useState({
    isEmail: false,
    isText: false,
    emailAddress: undefined,
    phoneNumber: undefined,
    isSoundAlert: false,
    sendAlertsEmail: false,
    sendAlertsText: false,
    sendAlertsSound: false,
  });

  //defaultSavedTpConfig
  const [savedConfigKey, setSavedConfigKey] = useState(0);
  //tpConfig
  const [tradePlanConfig, dispatchTradePlanConfig] = useTopReducer();
  const [restartSettings, setRestartSettings] = useRestartSettings();

  //options management
  //option filters
  const [tightSpreadPctEnabled, setTightSpreadPctEnabled] =
    useTightSpreadPctEnabled();
  const [tightSpreadCentsEnabled, setTightSpreadCentsEnabled] =
    useTightSpreadCentsEnabled();
  const [optionPriceRangeEnabled, setOptionPriceRangeEnabled] =
    useOptionPriceRangeEnabled();
  const [qtyRangeEnabled, setQtyRangeEnabled] = useQtyRangeEnabled();
  const [dollarRangeEnabled, setDollarRangeEnabled] = useDollarRangeEnabled();

  const [tightSpreadPct, dispatchTightSpreadPct] = useTightSpreadPct();
  const [tightSpreadCents, dispatchTightSpreadCents] = useTightSpreadCents();
  const [optionPriceRange, dispatchOptionPriceRange] = useOptionPriceRange();
  const [qtyRange, dispatchQtyRange] = useQtyRange();
  const [dollarRange, dispatchDollarRange] = useDollarRange();
  //optionChainStrategy
  const [hugCurrentPriceEnabled, setHugCurrentPriceEnabled] =
    useHugCurrentPriceEnabled();
  const [hugCurrentPrice, dispatchHugCurrentPrice] = useHugCurrentPrice();

  const [otm1Enabled, setOtm1Enabled] = useOtm1Enabled();
  const [otm1, dispatchOtm1] = useOtm1();

  const [itm1Enabled, setItm1Enabled] = useItm1Enabled();
  const [itm1, dispatchItm1] = useItm1();

  //option management
  const [viewOptionManagementEnabled, setViewOptionManagementEnabled] =
    useViewOptionManagementEnabled();
  const [expirationDates, setExpirationDates] = useExpirationDates();
  const [buyOrSellOption, setBuyOrSellOption] = useBuyOrSellOptions();
  const [otm, setOtm] = useOTM();
  const [itm, setItm] = useITM();
  const [notExpiringToday, setNotExpiringToday] = useNotExpiringToday(); //this is ONLY so frontend can use this. no need to pass to backend. will be set to undefined before sending

  let tpConfigOptionManagement = [
    tightSpreadPctEnabled,
    setTightSpreadPctEnabled,
    tightSpreadCentsEnabled,
    setTightSpreadCentsEnabled,
    optionPriceRangeEnabled,
    setOptionPriceRangeEnabled,
    tightSpreadPct,
    dispatchTightSpreadPct,
    tightSpreadCents,
    dispatchTightSpreadCents,
    optionPriceRange,
    dispatchOptionPriceRange,
    qtyRangeEnabled,
    setQtyRangeEnabled,
    dollarRangeEnabled,
    setDollarRangeEnabled,
    qtyRange,
    dispatchQtyRange,
    dollarRange,
    dispatchDollarRange,
    hugCurrentPriceEnabled,
    setHugCurrentPriceEnabled,
    hugCurrentPrice,
    dispatchHugCurrentPrice,
    setViewOptionManagementEnabled,
    expirationDates,
    setExpirationDates,
    buyOrSellOption,
    setBuyOrSellOption,
    otm,
    setOtm,
    itm,
    setItm,
    notExpiringToday,
    setNotExpiringToday,
    true,
    {}, //empty options bc irrelavent. the rest are empty functions/json to be able to reuse components
    () => {}, //set option choices
    {}, //option chain
    () => {}, //set option chain
    0, //symbols used
    () => {}, //set symbols used
    otm1Enabled,
    setOtm1Enabled,
    otm1,
    dispatchOtm1,
    itm1Enabled,
    setItm1Enabled,
    itm1,
    dispatchItm1,
  ];
  const tpConfigOptionMemo = useOptionManagement(
    [hugCurrentPriceEnabled, setHugCurrentPriceEnabled],
    [tightSpreadPctEnabled, setTightSpreadPctEnabled],
    [tightSpreadCentsEnabled, setTightSpreadCentsEnabled],
    [optionPriceRangeEnabled, setOptionPriceRangeEnabled],
    [qtyRangeEnabled, setQtyRangeEnabled],
    [dollarRangeEnabled, setDollarRangeEnabled],
    [hugCurrentPrice, dispatchHugCurrentPrice],
    [tightSpreadPct, dispatchTightSpreadPct],
    [tightSpreadCents, dispatchTightSpreadCents],
    [optionPriceRange, dispatchOptionPriceRange],
    [qtyRange, dispatchQtyRange],
    [dollarRange, dispatchDollarRange],
    [expirationDates, setExpirationDates],
    [buyOrSellOption, setBuyOrSellOption],
    [otm, setOtm],
    [itm, setItm],
    [notExpiringToday, setNotExpiringToday],
    [{}, () => {}],
    [otm1Enabled, setOtm1Enabled],
    [otm1, dispatchOtm1],
    [itm1Enabled, setItm1Enabled],
    [itm1, dispatchItm1]
  );

  //tpConfig entry tactic defaults
  const [marketOrderEnabled, setMarketOrderEnabled] = useMarketOrderEnabled();
  const [limitOrderEnabled, setLimitOrderEnabled] = useMarketOrderEnabled();
  const [limitOrderOffsetAmount, setLimitOrderOffsetAmount] = useState(0);
  const [setLimitFromSetupBreakPrice, setSetLimitFromSetupBreakPrice] =
    useState(false);
  const [candleBreakEnabled, setCandleBreakEnabled] = useMarketOrderEnabled();
  const [candleBreakCandleDuration, setCandleBreakCandleDuration] = useState(0);
  const [waitUntilCurrentCandleCloses, setWaitUntilCurrentCandleCloses] =
    useState(false);
  const [breakBy, setBreakBy] = useState(0.0);
  //sharesDollar
  //sharesQty
  //optionsDollar
  const [dollarStock, setDollarStock] = useState(0);
  const [qtyStock, setQtyStock] = useState(0);
  const [dollarOption, setDollarOption] = useState(0);
  const [qtyOption, setQtyOption] = useState(0);

  let entryTacticMemo = useMemo(() => {
    return {
      marketOrder: {
        enabled: marketOrderEnabled,
      },
      limitOrder: {
        enabled: limitOrderEnabled,
        offset: limitOrderOffsetAmount,
        setLimitFromSetupBreakPrice,
      },
      candleBreak: {
        enabled: candleBreakEnabled,
        candleDuration: candleBreakCandleDuration,
        waitUntilCurrentCandleCloses,
        breakBy,
      },
      qtyBasisValues: {
        dollarStock,
        dollarOption,
        qtyStock,
        qtyOption,
      },
    };
  }, [
    marketOrderEnabled,
    limitOrderEnabled,
    limitOrderOffsetAmount,
    setLimitFromSetupBreakPrice,
    candleBreakEnabled,
    candleBreakCandleDuration,
    waitUntilCurrentCandleCloses,
    breakBy,
    dollarStock,
    dollarOption,
    qtyStock,
    qtyOption,
  ]);

  //quick trade
  const [instrument, setInstrument] = useState('');
  const [quantityBasis, setQuantityBasis] = useState('');
  const [quantityBasisValue, setQuantityBasisValue] = useState(0);
  const [accountNumber, setAccountNumber] = useState('');
  const [dataSource, setDataSource] = useState('LIVE');
  const [brokerage, setBrokerage] = useState('');
  const [entryType, setEntryType] = useState('');
  const [limitOrder, dispatchLimitOrder] = useLimitOrder();
  const [candleBreak, dispatchCandleBreak] = useCandleBreak();
  const [Break, dispatchBreak] = useBreak();
  const [nearestOtmItm, setNearestOtmItm] = useState('nearestOtm');
  const [maxOptionPrice, setMaxOptionPrice] = useState(0);
  const [expirationWeeksAway, setExpirationWeeksAway] = useState(0);
  const [optionDirection, setOptionDirection] = useState('');

  const [optionNotExpiringToday, setOptionNotExpiringToday] =
    useNotExpiringToday(); //this is ONLY so frontend can use this. no need to pass to backend. will be set to undefined before sending
  const [workOrder, setWorkOrder] = useState(false);
  const [getAwayOffsetPct, setGetAwayOffsetPct] = useState(0.1);
  const [pctBetweenBidAsk, setPctBetweenBidAsk] = useState(0.5);
  const [pctIncreasePerOrderAttempt, setPctIncreasePerOrderAttempt] =
    useState(0.1);

  const quickTrade = useMemo(() => {
    return {
      instrument,
      quantityBasis,
      quantityBasisValue,
      accountNumber,
      dataSource,
      brokerage,
      optionDirection,
      entryType,
      limitOrder,
      candleBreak,
      break: Break,
      nearestOtmItm,
      maxOptionPrice,
      expirationWeeksAway,
      notExpiringToday: optionNotExpiringToday,
      workOrders: workOrder,
      getAwayOffsetPct,
      pctBetweenBidAsk,
      pctIncreasePerOrderAttempt,
    };
  }, [
    instrument,
    quantityBasis,
    quantityBasisValue,
    accountNumber,
    dataSource,
    brokerage,
    optionDirection,
    entryType,
    limitOrder,
    candleBreak,
    Break,
    nearestOtmItm,
    maxOptionPrice,
    expirationWeeksAway,
    optionNotExpiringToday,
    workOrder,
    getAwayOffsetPct,
    pctBetweenBidAsk,
    pctIncreasePerOrderAttempt,
  ]);

  //Exit Tactics
  const [lossLimitEnabled, setLossLimitEnabled] = useLossLimitEnabled();
  const [preserveProfitEnabled, setPreserveProfitEnabled] =
    usePreserveProfitEnabled();
  const [positionPriceTrailEnabled, setPositionPriceTrailEnabled] =
    usePositionPriceTrailEnabled();
  const [underlyingPriceTrailEnabled, setUnderlyingPriceTrailEnabled] =
    useUnderlyingPriceTrailEnabled();
  const [candleTrailEnabled, setCandleTrailEnabled] = useCandleTrailEnabled();
  const [atrTrailEnabled, setAtrTrailEnabled] = useAtrTrailEnabled();
  const [grabCentsEnabled, setGrabCentsEnabled] = useGrabCentsEnabled();
  const [greenToRedCandlesEnabled, setGreenToRedCandlesEnabled] =
    useGreenToRedCandlesEnabled();
  const [greenToRedPositionsEnabled, setGreenToRedPositionsEnabled] =
    useGreenToRedPositionsEnabled();
  const [breakPriceEnabled, setBreakPriceEnabled] = useBreakPriceEnabled();
  const [positionBreakPriceEnabled, setPositionBreakPriceEnabled] =
    usePositionBreakPriceEnabled();
  const [candleMagnitudeEnabled, setCandleMagnitudeEnabled] =
    useCandleMagnitudeEnabled();
  const [ratchetBracketEnabled, setRatchetBracketEnabled] =
    useRatchetBracketEnabled();
  const [mfeRetracementEnabled, setMfeRetracementEnabled] =
    useMfeRetracementEnabled();
  const [underlyingPriceExitEnabled, setUnderlyingPriceExitEnabled] =
    useUnderlyingPriceExitEnabled();

  const [grabCentsMode, setGrabCentsMode] = useGrabCentsMode();
  const [lossLimit, dispatchLossLimit] = useLossLimit();
  const [preserveProfit, dispatchPreserveProfit] = usePreserveProfit();
  const [positionPriceTrail, dispatchPositionPriceTrail] =
    usePositionPriceTrail();
  const [underlyingPriceTrail, dispatchUnderlyingPriceTrail] =
    useUnderlyingPriceTrail();
  const [candleTrail, dispatchCandleTrail] = useCandleTrail();
  const [grabCents, dispatchGrabCents] = useGrabCents();
  const [greenToRedCandles, dispatchGreenToRedCandles] = useGreenToRedCandles();
  const [greenToRedPositions, dispatchGreenToRedPositions] =
    useGreenToRedPositions();
  const [breakPrice, dispatchBreakPrice] = useBreakPrice();
  const [positionBreakPrice, dispatchPositionBreakPrice] =
    usePositionBreakPrice();
  const [candleMagnitude, dispatchCandleMagnitude] = useCandleMagnitude();
  const [ratchetBracket, dispatchRatchetBracket] = useRatchetBracket();
  const grabCentsDetails = useGrabCentsDetails(
    [grabCents, dispatchGrabCents],
    [grabCentsMode, setGrabCentsMode]
  );
  const [atrTrail, dispatchAtrTrail] = useAtrTrail();
  const [mfeRetracement, dispatchMfeRetracement] = useMfeRetracement();
  const [underlyingPriceExit, dispatchUnderlyingPriceExit] =
    useUnderlyingPriceExit();

  const exitTactics = useExitTactics(
    [lossLimitEnabled, setLossLimitEnabled],
    [lossLimit, dispatchLossLimit],
    [preserveProfitEnabled, setPreserveProfitEnabled],
    [preserveProfit, dispatchPreserveProfit],
    [positionPriceTrailEnabled, setPositionPriceTrailEnabled],
    [positionPriceTrail, dispatchPositionPriceTrail],
    [underlyingPriceTrailEnabled, setUnderlyingPriceTrailEnabled],
    [underlyingPriceTrail, dispatchUnderlyingPriceTrail],
    [grabCentsEnabled, setGrabCentsEnabled],
    [grabCents, dispatchGrabCents],
    grabCentsDetails,
    [ratchetBracketEnabled, setRatchetBracketEnabled],
    [ratchetBracket, dispatchRatchetBracket],
    [candleTrailEnabled, setCandleTrailEnabled],
    [candleTrail, dispatchCandleTrail],
    [greenToRedCandlesEnabled, setGreenToRedCandlesEnabled],
    [greenToRedPositionsEnabled, setGreenToRedPositionsEnabled],
    [greenToRedCandles, dispatchGreenToRedCandles],
    [greenToRedPositions, dispatchGreenToRedPositions],
    [breakPriceEnabled, setBreakPriceEnabled],
    [breakPrice, dispatchBreakPrice],
    [positionBreakPriceEnabled, setPositionBreakPriceEnabled],
    [positionBreakPrice, dispatchPositionBreakPrice],
    [candleMagnitudeEnabled, setCandleMagnitudeEnabled],
    [candleMagnitude, dispatchCandleMagnitude],
    [atrTrailEnabled, setAtrTrailEnabled],
    [atrTrail, dispatchAtrTrail],
    [mfeRetracementEnabled, setMfeRetracementEnabled],
    [mfeRetracement, dispatchMfeRetracement],
    [underlyingPriceExitEnabled, setUnderlyingPriceExitEnabled],
    [underlyingPriceExit, dispatchUnderlyingPriceExit]
  );
  //options radio buttons

  const [defaultOptionRadio, setDefaultOptionRadio] = useState('none');

  const userPreferences = useMemo(() => {
    return {
      exitTactics,
      quickTrade,
      tradePlanConfig,
      optionManagement: tpConfigOptionMemo,
      tradePlanDefaultEntryTactics: entryTacticMemo,
      optionRadioButtons: defaultOptionRadio,
      defaultSavedTpConfigKey: savedConfigKey,
      restartSettings,
      userAlerts,
    };
  }, [
    exitTactics,
    quickTrade,
    tradePlanConfig,
    tpConfigOptionMemo,
    entryTacticMemo,
    defaultOptionRadio,
    savedConfigKey,
    restartSettings,
    userAlerts,
  ]);

  return {
    userPreferences,
    tradePlanConfig: {
      tradePlanConfig,
      dispatchTradePlanConfig,
    },
    tradePlanDefaultEntryTactics: {
      marketOrderEnabled,
      setMarketOrderEnabled,
      limitOrderEnabled,
      setLimitOrderEnabled,
      limitOrderOffsetAmount,
      setLimitOrderOffsetAmount,
      setLimitFromSetupBreakPrice,
      setSetLimitFromSetupBreakPrice,
      candleBreakEnabled,
      setCandleBreakEnabled,
      candleBreakCandleDuration,
      setCandleBreakCandleDuration,
      waitUntilCurrentCandleCloses,
      setWaitUntilCurrentCandleCloses,
      breakBy,
      setBreakBy,
      dollarStock,
      setDollarStock,
      dollarOption,
      setDollarOption,
      qtyStock,
      setQtyStock,
      qtyOption,
      setQtyOption,
    },
    optionManagement: { tpConfigOptionManagement },
    exitTactics: {
      lossLimit,
      dispatchLossLimit,
      lossLimitEnabled,
      setLossLimitEnabled,
      preserveProfit,
      dispatchPreserveProfit,
      preserveProfitEnabled,
      setPreserveProfitEnabled,
      positionPriceTrail,
      dispatchPositionPriceTrail,
      positionPriceTrailEnabled,
      setPositionPriceTrailEnabled,
      underlyingPriceTrail,
      dispatchUnderlyingPriceTrail,
      underlyingPriceTrailEnabled,
      setUnderlyingPriceTrailEnabled,
      candleTrail,
      dispatchCandleTrail,
      candleTrailEnabled,
      setCandleTrailEnabled,
      grabCents,
      dispatchGrabCents,
      grabCentsEnabled,
      setGrabCentsEnabled,
      grabCentsMode,
      setGrabCentsMode,
      greenToRedCandles,
      dispatchGreenToRedCandles,
      greenToRedCandlesEnabled,
      setGreenToRedCandlesEnabled,
      greenToRedPositions,
      dispatchGreenToRedPositions,
      greenToRedPositionsEnabled,
      setGreenToRedPositionsEnabled,
      breakPrice,
      dispatchBreakPrice,
      breakPriceEnabled,
      setBreakPriceEnabled,
      positionBreakPrice,
      dispatchPositionBreakPrice,
      positionBreakPriceEnabled,
      setPositionBreakPriceEnabled,
      candleMagnitude,
      dispatchCandleMagnitude,
      candleMagnitudeEnabled,
      setCandleMagnitudeEnabled,
      ratchetBracketEnabled,
      setRatchetBracketEnabled,
      ratchetBracket,
      dispatchRatchetBracket,
      atrTrailEnabled,
      setAtrTrailEnabled,
      atrTrail,
      dispatchAtrTrail,
      mfeRetracementEnabled,
      setMfeRetracementEnabled,
      mfeRetracement,
      dispatchMfeRetracement,
      underlyingPriceExitEnabled,
      setUnderlyingPriceExitEnabled,
      underlyingPriceExit,
      dispatchUnderlyingPriceExit,
    },
    quickTrade: {
      instrument,
      setInstrument,
      quantityBasis,
      setQuantityBasis,
      quantityBasisValue,
      setQuantityBasisValue,
      accountNumber,
      setAccountNumber,
      dataSource,
      setDataSource,
      brokerage,
      setBrokerage,
      optionDirection,
      setOptionDirection,
      entryType,
      setEntryType,
      limitOrder,
      dispatchLimitOrder,
      candleBreak,
      dispatchCandleBreak,
      Break,
      dispatchBreak,
      nearestOtmItm,
      setNearestOtmItm,
      maxOptionPrice,
      setMaxOptionPrice,
      expirationWeeksAway,
      setExpirationWeeksAway,
      optionNotExpiringToday,
      setOptionNotExpiringToday,
      workOrder,
      setWorkOrder,
      pctIncreasePerOrderAttempt,
      setPctIncreasePerOrderAttempt,
      pctBetweenBidAsk,
      setPctBetweenBidAsk,
      getAwayOffsetPct,
      setGetAwayOffsetPct,
    },
    optionRadioButtons: {
      defaultOptionRadio,
      setDefaultOptionRadio,
    },
    defaultSavedTpConfigKey: {
      savedConfigKey,
      setSavedConfigKey,
    },
    restartSettings: {
      restartSettings,
      setRestartSettings,
    },
    userAlerts: { userAlerts, setUserAlerts },
  };
};

export default useUserPreferences;
